<script setup lang='ts'>
import { USER_TYPE, articleAuthorPath, searchUserPath } from '@qcwp/common'
import { isClient, numFormat, timeFormat, timeFrom } from '@qcwp/utils'
import { useElementVisibility } from '@vueuse/core'
import { LogicUserMediaAttention } from '~~/src/logic/article/detail'
import type { ArticleWorkerVO } from '~~/src/server/modules/article/type'
import { WORKER_TYPE_TEXT } from '~~/src/common/const'

const props = withDefaults(defineProps<{
  aiHelperType?: '0' | '1'
  mediaId: string // 作者 id
  mediaAvatar?: string
  mediaName?: string // 作者
  mediaType?: USER_TYPE // 作者 id
  authorName?: string // 作者
  sysUserName?: string // 编辑
  isFocus?: boolean // 是否关注
  look?: number
  time?: string
  type?: 'article' | 'video' | 'live'
  isShowButton?: boolean
  isPoster?: boolean // 是否是生成海报
  mediaGrade?: string
  worker?: ArticleWorkerVO[]
  updateTime?: string | null
}>(), {
  aiHelperType: '1',
  mediaAvatar: '',
  mediaName: '',
  mediaType: USER_TYPE.INDIVIDUAL,
  isFocus: false,
  look: 0,
  isShowButton: true,
  isPoster: false,
  mediaGrade: '0',
  worker: () => [],
  updateTime: '',
})
const emits = defineEmits({
  mediaRecommendAttentionStatusChange: (status: boolean) => typeof status === 'boolean',
})

const { isMobileOrTablet } = useDevice()
const targetRef = ref()
const targetIsVisible = useElementVisibility(targetRef)

const lookText: Record<'article' | 'video' | 'live', string> = {
  article: '阅读',
  video: '播放',
  live: '观看',
}

const POSTER_OFFSET_STYLE = {
  down: [props.isPoster ? 'relative top-2' : ''],
  up: [props.isPoster ? 'relative -top-2' : ''],
}

async function focusHandel(status: boolean) {
  const result = await LogicUserMediaAttention(props.mediaId, status)
  if (result)
    emits('mediaRecommendAttentionStatusChange', status)
}

const qrcode = ref('')
async function initHandler() {
  if (isClient)
    qrcode.value = await generateCode(window.location.href) || ''
}
function workerFilter(type: string) {
  return props.worker?.filter(v => v.workerType === type) || []
}
const worker_editor = computed(() => workerFilter('1'))
const worker_host = computed(() => workerFilter('2'))

initHandler()
</script>

<template>
  <client-only>
    <!-- 添加 opacity-0 避免未知原因的模糊过渡  -->
    <div class="md:hidden fixed top-0 left-0 right-0 bg-white h-12 fixed " :class="[!targetIsVisible ? 'z-1000' : 'opacity-0']">
      <layout-nav-back-nav :hide-home="true">
        <client-only>
          <div class="flex items-center">
            <NuxtLink class="flex-grow w-0 flex items-center " :to="articleAuthorPath(props.mediaId || '')">
              <utils-media-avatar
                class="flex-shrink-0"
                :size="30"
                :name="props.mediaName"
                :src="props.mediaAvatar"
                :type="props.mediaType"
                :media-grade="props.mediaGrade"
              />
              <div class="ml-3 text-3.75 truncate font-bold">
                {{ props.mediaName }}
              </div>
            </NuxtLink>
            <div class="ml-3 flex-shrink-0">
              <n-tag v-if="!props.isFocus" size="small" round type="success" :title="`关注网评号${props.mediaName}`" @click="focusHandel(true)">
                + 关注
              </n-tag>
              <n-tag v-else size="small" round :title="`取消关注网评号${props.mediaName}`" @click="focusHandel(false)">
                已关注
              </n-tag>
            </div>
          </div>
        </client-only>
      </layout-nav-back-nav>
    </div>
  </client-only>
  <div ref="targetRef" v-bind="$attrs" flex items-center justify-between>
    <div flex items-start>
      <NuxtLink text-0 :to="articleAuthorPath(props.mediaId || '')">
        <utils-media-avatar
          :size="45"
          :class="POSTER_OFFSET_STYLE.down"
          :name="props.mediaName"
          :src="props.mediaAvatar"
          :type="props.mediaType"
          :is-poster="isPoster"
          :media-grade="props.mediaGrade"
        />
      </NuxtLink>
      <div ml-3>
        <div class="flex items-center">
          <NuxtLink
            class="public-hover font-bold text-3.5 md:text-4"
            :to="articleAuthorPath(props.mediaId || '')"
          >
            {{ props.mediaName }}
          </NuxtLink>
          <n-tag
            v-if="props.mediaType === USER_TYPE.SELF && String(props.aiHelperType) === '0'"
            size="small" :bordered="false" type="info"
            class="ml-3" :class="POSTER_OFFSET_STYLE.down"
          >
            <span :class="POSTER_OFFSET_STYLE.up">原创</span>
          </n-tag>
          <client-only>
            <div v-if="props.isShowButton && !isPoster && isMobileOrTablet" flex="~ shrink-0" ml-2 md="hidden">
              <n-tag v-if="!isFocus" size="small" round type="success" :title="`关注网评号${props.mediaName}`" @click="focusHandel(true)">
                + 关注
              </n-tag>
              <n-tag v-else size="small" round :title="`取消关注网评号${props.mediaName}`" @click="focusHandel(false)">
                已关注
              </n-tag>
            </div>
          </client-only>
        </div>
        <div class="text-3 text-title-500 flex flex-wrap items-center mt-1">
          <p class="mr-2 md:mr-4">
            {{ numFormat(look) }}{{ (props.type ? lookText[props.type] : '') || '阅读' }}
          </p>
          <p v-if="time" class="mr-2 md:mr-4">
            <span> {{ props.type === 'live' ? '开始时间' : '' }}</span>
            <span v-if="!isPoster"> {{ timeFrom(time, props.type === 'live' ? 'YYYY-MM-DD HH:mm:ss' : undefined) }}</span>
            <span v-else> {{ timeFormat(time, 'YYYY-MM-DD') }}</span>
          </p>
          <p v-if="updateTime" class="mr-2 md:mr-4">
            <n-popover trigger="hover">
              <template #trigger>
                已编辑
              </template>
              <span>{{ updateTime }}</span>
            </n-popover>
          </p>
          <template v-if="worker?.length">
            <div v-if="worker_editor.length" class="mr-2 md:mr-4">
              {{ WORKER_TYPE_TEXT[1] }}：{{ worker_editor.map(v => v.userName).join('、') }}
            </div>
            <div v-if="worker_host.length" class="mr-2 md:mr-4">
              {{ WORKER_TYPE_TEXT[2] }}：{{ worker_host.map(v => v.userName).join('、') }}
            </div>
          </template>
          <template v-else-if="props.authorName && props.authorName === props.sysUserName">
            <nuxt-link class="mr-2 md:mr-4" :href="searchUserPath({ keyword: props.sysUserName })" target="_blank">
              作者/编辑：{{ props.authorName }}
            </nuxt-link>
          </template>
          <template v-else>
            <nuxt-link v-if="props.authorName" class="mr-2 md:mr-4" :href="searchUserPath({ keyword: props.authorName })" target="_blank">
              作者：{{ props.authorName }}
            </nuxt-link>
            <nuxt-link v-if="props.sysUserName" class="mr-2 md:mr-4" :href="searchUserPath({ keyword: props.sysUserName })" target="_blank">
              编辑：{{ props.sysUserName }}
            </nuxt-link>
          </template>
          <ClientOnly v-if="!isMobileOrTablet">
            <n-popover trigger="hover" display-directive="show" :show-arrow="false" raw>
              <template #trigger>
                <div class="text-blue-5 cursor-pointer flex items-center">
                  <i class="i-carbon:send-alt-filled" />
                  手机上浏览
                </div>
              </template>
              <div bg-white>
                <img w-30 h-30 :src="qrcode">
              </div>
            </n-popover>
          </ClientOnly>
        </div>
      </div>
    </div>
    <client-only>
      <div v-if="isShowButton && !isPoster && !isMobileOrTablet" flex="~ shrink-0" class="items-center gap-2 hidden" md="block gap-4">
        <button v-if="!isFocus" title="关注" class="btn-primary" lt-sm="text-3 px-2 py-1" @click="focusHandel(true)">
          关注
        </button>
        <button v-else title="取消关注" class="btn-waring " lt-sm="text-3 px-2 py-1" @click="focusHandel(false)">
          取消关注
        </button>
      </div>
    </client-only>
  </div>
</template>
